// src/adminDashboard/shared/utils/apiHelpers.ts
// Shared API response parsing utilities

export interface PaginationLinks {
    first?: string | undefined;
    last?: string | undefined;
    prev?: string | null | undefined;
    next?: string | null | undefined;
}

export interface PaginationMeta {
    current_page?: number | undefined;
    from?: number | undefined;
    last_page?: number | undefined;
    links?: Array<{
        url: string | null;
        label: string;
        active: boolean;
    }> | undefined;
    path?: string | undefined;
    per_page?: number | undefined;
    to?: number | undefined;
    total?: number | undefined;
}

export interface ListResponse<
    T = never,
    S = Record<string, unknown>
> {
    items: T[];
    total: number;
    currentPage?: number | undefined;
    lastPage?: number | undefined;
    perPage?: number | undefined;
    meta?: PaginationMeta | undefined;
    links?: PaginationLinks | undefined;
    summary?: S | undefined;
    filters?: Record<string, unknown> | undefined;
    pagination?: PaginationMeta | undefined;
}

export interface BaseApiResponse {
    success?: boolean;
    message?: string;
    data?: unknown;
    meta?: PaginationMeta;
    links?: PaginationLinks;
    total?: number;
    current_page?: number;
    last_page?: number;
    per_page?: number;
    summary?: Record<string, unknown>;
    stats?: Record<string, unknown>;
    filters?: Record<string, unknown>;
    pagination?: PaginationMeta;
}

/**
 * Parse API response data that can come in different formats:
 * - { data: [...] } - flat array
 * - { data: { data: [...], total: X, ... } } - Laravel pagination (respondSuccess)
 * - { data: [...], meta: { total: X, ... } } - Laravel Resource Collection
 * - { success: true, data: [...], total: X, ... } - respondSuccess format
 * - [...] - raw array
 */
export const parseListResponse = <
    T = never,
    S = Record<string, unknown>
>(
    response: unknown
): ListResponse<T, S> => {
    // Handle empty responses
    if (!response) {
        return { items: [], total: 0 };
    }

    const isPlainObject = (value: unknown): value is Record<string, unknown> =>
        !!value && typeof value === 'object' && !Array.isArray(value);

    const looksLikeApiPayload = (value: unknown): value is Record<string, unknown> => {
        if (!isPlainObject(value)) {
            return false;
        }

        const obj = value as Record<string, unknown>;
        return (
            obj.success !== undefined ||
            obj.message !== undefined ||
            obj.summary !== undefined ||
            obj.meta !== undefined ||
            obj.links !== undefined ||
            obj.total !== undefined ||
            obj.pagination !== undefined ||
            Array.isArray(obj.data)
        );
    };

    // First, check if this is an axios-wrapped response or direct API response
    let apiResponse: BaseApiResponse = {};

    if (isPlainObject(response)) {
        const axiosResponse = response as { data?: unknown };
        if (axiosResponse?.data && looksLikeApiPayload(axiosResponse.data) && !looksLikeApiPayload(response)) {
            apiResponse = axiosResponse.data as BaseApiResponse;
        } else {
            apiResponse = response as BaseApiResponse;
        }
    }

    const summary = (apiResponse?.summary ||
        apiResponse?.stats ||
        (isPlainObject(response) ? (response.summary as Record<string, unknown>) : undefined) ||
        (isPlainObject(response) ? (response.stats as Record<string, unknown>) : undefined) ||
        (isPlainObject(response) && isPlainObject(response.data)
            ? (response.data as Record<string, unknown>).summary as Record<string, unknown>
            : undefined) ||
        (isPlainObject(response) && isPlainObject(response.data)
            ? (response.data as Record<string, unknown>).stats as Record<string, unknown>
            : undefined)) as S | undefined;

    const filters =
        apiResponse?.filters ||
        (isPlainObject(response) ? (response.filters as Record<string, unknown>) : undefined) ||
        (isPlainObject(response) && isPlainObject(response.data)
            ? (response.data as Record<string, unknown>).filters as Record<string, unknown>
            : undefined);

    // If response is already an array (no wrapper at all)
    if (Array.isArray(response)) {
        return { items: response as T[], total: response.length, summary, filters };
    }

    // Handle custom pagination format: { data: [...], pagination: { total, current_page, ... } }
    if (apiResponse?.pagination?.total !== undefined && Array.isArray(apiResponse.data)) {
        const pagination = apiResponse.pagination as PaginationMeta;
        return {
            items: apiResponse.data,
            total: pagination.total ?? apiResponse.data.length,
            currentPage: pagination.current_page,
            lastPage: pagination.last_page,
            perPage: pagination.per_page,
            pagination,
            summary,
            filters,
        };
    }

    // Handle Resource Collection format: { data: [...], meta: { total, current_page, ... }, links: {...} }
    const metaObj = apiResponse?.meta;
    if (metaObj && metaObj.total !== undefined) {
        const items = Array.isArray(apiResponse.data) ? (apiResponse.data as T[]) : [];
        const meta = metaObj;
        return {
            items,
            total: meta.total ?? 0,
            currentPage: meta.current_page,
            lastPage: meta.last_page,
            perPage: meta.per_page,
            meta,
            links: apiResponse.links as PaginationLinks,
            summary,
            filters,
        };
    }

    // Handle respondSuccess format: { success: true, data: [...], total: X, per_page: Y, ... }
    if (apiResponse?.total !== undefined && Array.isArray(apiResponse.data)) {
        return {
            items: apiResponse.data as T[],
            total: apiResponse.total as number,
            currentPage: apiResponse.current_page as number,
            lastPage: apiResponse.last_page as number,
            perPage: apiResponse.per_page as number,
            summary,
            filters,
        };
    }

    // Handle nested data format: { data: { data: [...], total: X } }
    const responseData = apiResponse?.data as Record<string, unknown> | undefined;
    if (responseData && responseData.data !== undefined) {
        const nestedData = responseData.data;
        const items = Array.isArray(nestedData) ? (nestedData as T[]) : [];
        const meta = responseData.meta as PaginationMeta | undefined;
        return {
            items,
            total: (responseData.total as number) ?? meta?.total ?? items.length,
            currentPage: (responseData.current_page as number) ?? meta?.current_page,
            lastPage: (responseData.last_page as number) ?? meta?.last_page,
            perPage: (responseData.per_page as number) ?? meta?.per_page,
            meta,
            summary,
            filters,
        };
    }

    // If apiResponse has a data property that's an array (simple wrapper without pagination)
    if (apiResponse?.data !== undefined && Array.isArray(apiResponse.data)) {
        return {
            items: apiResponse.data,
            total: apiResponse.data.length,
            summary,
            filters,
        };
    }

    // Fallback: empty result
    console.warn('parseListResponse: Could not parse response', apiResponse);
    return { items: [], total: 0, summary, filters };
};

/**
 * Parse single item response
 */
export const parseSingleResponse = <T = unknown>(response: unknown): T | null => {
    if (!response) return null;
    const resp = response as Record<string, unknown>;
    const data = (resp?.data ?? response) as Record<string, unknown>;
    return (data?.data ?? data) as T;
};

/**
 * Build URL params from an object, filtering out empty values
 */
export const buildParams = (params: Record<string, unknown>): URLSearchParams => {
    const filtered = Object.entries(params).reduce((acc: Record<string, unknown>, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            acc[key] = value;
        }
        return acc;
    }, {});
    return new URLSearchParams(filtered as Record<string, string>);
};

const apiHelpers = {
    parseListResponse,
    parseSingleResponse,
    buildParams,
};

export default apiHelpers;
